import * as React from "react";
import { Input } from "./input";

/**
 * NumericInput — OCI V2.13 single-source numeric field.
 *
 * Fixes the platform-wide "undeletable leading digit" defect caused by the
 * `parseInt(e.target.value) || default` anti-pattern, where emptying the field
 * yields NaN and the `||` immediately rehydrates the default, so the final
 * digit can never be removed.
 *
 * Contract:
 *   - value: number | "" | null | undefined  (controlled by parent)
 *   - onValueChange(v): receives a number while valid, or "" while empty.
 *       => Empty stays empty WHILE TYPING (no silent default coercion).
 *   - fallback: number applied ONLY on blur if the field is left empty/invalid
 *       (i.e. at the validation boundary, never mid-typing).
 *   - integer: when true uses parseInt (whole numbers), else parseFloat.
 *
 * All other props (className, min, max, step, placeholder, disabled,
 * data-testid, ...) are forwarded to the underlying shadcn Input.
 */
export const NumericInput = React.forwardRef(function NumericInput(
  { value, onValueChange, fallback = 0, integer = false, onBlur, onFocus, ...props },
  ref
) {
  const isFocused = React.useRef(false);
  const toStr = (v) =>
    v === "" || v === null || v === undefined ? "" : String(v);
  const [draft, setDraft] = React.useState(() => toStr(value));

  // Re-sync the draft from the external value ONLY when not actively editing,
  // so loading/saving updates the display without clobbering in-progress edits.
  React.useEffect(() => {
    if (!isFocused.current) setDraft(toStr(value));
  }, [value]);

  const parse = (raw) => (integer ? parseInt(raw, 10) : parseFloat(raw));

  const handleChange = (e) => {
    const raw = e.target.value;
    setDraft(raw);
    if (raw === "" || raw === "-" || raw === ".") {
      onValueChange("");
      return;
    }
    const num = parse(raw);
    if (!Number.isNaN(num)) onValueChange(num);
  };

  const handleFocus = (e) => {
    isFocused.current = true;
    onFocus && onFocus(e);
  };

  const handleBlur = (e) => {
    isFocused.current = false;
    const num = parse(draft);
    if (draft === "" || Number.isNaN(num)) {
      setDraft(toStr(fallback));
      onValueChange(fallback);
    } else {
      setDraft(toStr(num));
    }
    onBlur && onBlur(e);
  };

  return (
    <Input
      ref={ref}
      type="number"
      inputMode={integer ? "numeric" : "decimal"}
      value={draft}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    />
  );
});
